import "./service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";
import Icon from "../../components/Icon";
import useReveal from "../../hooks/useReveal";

const offerings = [
  {
    icon: "cloud",
    title: "AWS Infrastructure Design",
    desc: "Production AWS architecture built around your workload — VPC networking, IAM boundaries, managed databases, and storage tiers provisioned as code rather than clicked together by hand.",
  },
  {
    icon: "layers",
    title: "Containerised Deployment",
    desc: "Applications packaged with Docker and run on ECS, EKS, or Fargate, so the environment that passes your tests is the same one that serves your users.",
  },
  {
    icon: "workflow",
    title: "CI/CD Pipelines",
    desc: "Automated build, test, and release pipelines with staged rollouts and one-command rollback, so shipping is routine instead of an event.",
  },
  {
    icon: "trending",
    title: "Autoscaling & Load Balancing",
    desc: "Horizontal scaling policies and load distribution tuned to real traffic patterns — capacity follows demand instead of being guessed in advance.",
  },
  {
    icon: "activity",
    title: "Observability & Monitoring",
    desc: "Metrics, structured logs, distributed traces, and alerting wired in from day one, so you find problems before your customers report them.",
  },
  {
    icon: "gauge",
    title: "Cost Optimisation",
    desc: "Right-sized instances, spot and reserved capacity where it fits, and storage lifecycle rules — cloud spend that tracks usage rather than drifting upward.",
  },
];

const techGroups = [
  {
    label: "AWS Services",
    items: ["EC2", "ECS / Fargate", "EKS", "Lambda", "RDS", "S3", "CloudFront"],
  },
  {
    label: "Containers & Orchestration",
    items: ["Docker", "Kubernetes", "Helm", "Nginx"],
  },
  {
    label: "Infrastructure as Code",
    items: ["Terraform", "AWS CDK", "CloudFormation"],
  },
  {
    label: "CI/CD & Observability",
    items: ["GitHub Actions", "CloudWatch", "Prometheus", "Grafana", "Sentry"],
  },
];

const keyFeatures = [
  {
    icon: "shield",
    title: "Secure by Default",
    desc: "Least-privilege IAM roles, encrypted storage and transit, isolated network boundaries, and secrets kept out of source control — security decisions made at provisioning time, not retrofitted.",
  },
  {
    icon: "code",
    title: "Infrastructure as Code",
    desc: "Every environment defined in version-controlled templates, so staging genuinely matches production and a rebuild is reproducible rather than remembered.",
  },
  {
    icon: "bolt",
    title: "Zero-Downtime Releases",
    desc: "Blue-green and rolling deployment strategies that ship changes without a maintenance window, with automated health checks gating every step.",
  },
  {
    icon: "database",
    title: "Backup & Disaster Recovery",
    desc: "Automated snapshots, point-in-time restore, and multi-AZ redundancy — with recovery procedures that have actually been tested.",
  },
];

export default function CloudDeployment() {
  const [offeringsRef, offeringsVisible] = useReveal();
  const [featuresRef, featuresVisible] = useReveal();

  return (
    <>
      <SEO
        title="Cloud Deployment: AWS, Docker & Kubernetes | Best Code Technology"
        description="Production cloud infrastructure on AWS — containerised deployment, CI/CD pipelines, autoscaling, observability, and cost optimisation."
        keywords="cloud deployment, AWS infrastructure, Docker, Kubernetes, CI/CD, DevOps, infrastructure as code"
        url="https://bestcodetechnology.in/services/cloud-deployment"
      />

      <div className="svc-page">
        {/* ── Hero ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <div className="svc-hero__eyebrow">
                  <span className="pill-label">Cloud &amp; Infrastructure</span>
                </div>
                <h1 className="svc-hero__headline">
                  Ship to Production on{" "}
                  <span>Infrastructure That Scales</span>
                </h1>
                <p className="svc-hero__desc">
                  We design, provision, and operate cloud infrastructure on AWS —
                  containerised workloads, automated pipelines, and observability
                  built in. The result is a platform your team can deploy to
                  confidently, many times a day.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── What We Offer ── */}
        <section className="svc-offerings">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">
                  What We Offer
                </span>
                <h2 className="svc-section-title">Cloud Engineering</h2>
                <p className="svc-section-sub">
                  End-to-end cloud infrastructure services covering architecture,
                  deployment automation, scaling, monitoring, and ongoing cost
                  control.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={offeringsRef}>
              {offerings.map((item, i) => (
                <div
                  key={item.title}
                  className={`col-12 col-md-6 col-xl-4 reveal ${
                    offeringsVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-offer-card h-100">
                    <div className="svc-offer-card__header">
                      <div className="svc-offer-card__icon">
                        <Icon name={item.icon} size={22} />
                      </div>
                    </div>
                    <div
                      className="svc-offer-card__divider"
                      aria-hidden="true"
                    />
                    <h4 className="svc-offer-card__title">{item.title}</h4>
                    <p className="svc-offer-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Tech Stack ── */}
        <section className="svc-tech">
          <div className="container-xl">
            <div className="row align-items-start g-5">
              <div className="col-12 col-lg-4 svc-tech__col-left pe-lg-5">
                <span className="pill-label svc-section-label">
                  Technologies
                </span>
                <h2 className="svc-section-title">Our Cloud Technology Stack</h2>
                <p className="svc-section-sub svc-section-sub--left">
                  Managed AWS services and open standards chosen for reliability,
                  portability, and operational cost over the life of the system.
                </p>
              </div>

              <div className="col-12 col-lg-8">
                <div className="svc-tech__groups">
                  {techGroups.map((group) => (
                    <div key={group.label} className="svc-tech__group">
                      <span className="svc-tech__group-label">
                        {group.label}
                      </span>
                      <div className="svc-tech__badges">
                        {group.items.map((tech) => (
                          <span key={tech} className="svc-tech__badge">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Key Features ── */}
        <section className="svc-usecases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">
                  How We Build
                </span>
                <h2 className="svc-section-title">
                  Engineered for Uptime and Change
                </h2>
                <p className="svc-section-sub">
                  Every environment we deliver is reproducible, observable, and
                  safe to deploy to during business hours.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={featuresRef}>
              {keyFeatures.map((feat, i) => (
                <div
                  key={feat.title}
                  className={`col-12 col-lg-6 reveal ${
                    featuresVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-usecase-card h-100">
                    <div className="svc-usecase-card__top">
                      <div className="svc-usecase-card__icon">
                        <Icon name={feat.icon} size={22} />
                      </div>
                    </div>
                    <h3 className="svc-usecase-card__title">{feat.title}</h3>
                    <p className="svc-usecase-card__desc">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
